import { action, makeObservable, observable, runInAction } from 'mobx';
import { directoryAxiosClient } from 'Services/Api/client';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import {
	DirectoryGroup,
	DirectoryRole,
	DirectoryUser
} from 'Services/Api/Directory/Types';
import { Status } from 'app-types';

class AssignUsersStore {
	status = {
		fetchOrganizations: Status.Idle,
		fetchAssignmentOrganizations: Status.Idle,
		fetchProperties: Status.Idle,
		fetchPropertiesByTenant: Status.Idle,
		fetchRoles: Status.Idle,
		fetchGroups: Status.Idle,
		fetchUsers: Status.Idle,
		fetchUsersByIds: Status.Idle
	};

	organizations: DirectoryGroup[] = [];
	// The organizations an assignment can target. Isolated from `organizations`
	// — that list is every tenant, and it backs the tenant picker itself, so
	// narrowing the create flow to one tenant must not shrink the picker that
	// chose it.
	assignmentOrganizations: DirectoryGroup[] = [];
	properties: DirectoryGroup[] = [];
	// Tenant `properties` was loaded for, so callers can tell a warm list from
	// one belonging to a different tenant. Not observable — bookkeeping.
	propertiesTenantId: number | undefined = undefined;
	// Isolated from `properties` so the two pickers never overwrite each other:
	// this one always belongs to one named tenant (the Save Report modal), while
	// `properties` follows whatever the create flow is currently scoped to.
	tenantProperties: DirectoryGroup[] = [];
	roles: DirectoryRole[] = [];
	groups: DirectoryGroup[] = [];
	users: DirectoryUser[] = [];
	// Isolated from `users` so the create-flow property picker is never affected.
	assignmentUsers: DirectoryUser[] = [];

	constructor() {
		makeObservable(this, {
			status: observable,
			organizations: observable,
			assignmentOrganizations: observable,
			properties: observable,
			tenantProperties: observable,
			roles: observable,
			groups: observable,
			users: observable,
			assignmentUsers: observable
		});
	}

	@action
	async fetchOrganizations(): Promise<void> {
		runInAction(() => {
			this.status.fetchOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.organizations = data;
				this.status.fetchOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	/**
	 * The organizations a new assignment can target. With `tenantId` the list is
	 * that one tenant's organization; without it the caller's own token decides
	 * what comes back.
	 */
	@action
	async fetchAssignmentOrganizations(tenantId?: number): Promise<void> {
		runInAction(() => {
			this.status.fetchAssignmentOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&label[]=Organization`
					: `${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.assignmentOrganizations = data;
				this.status.fetchAssignmentOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchAssignmentOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	/**
	 * `tenantId` narrows the list to that tenant's properties, walking down its
	 * group tree the same way `fetchPropertiesByTenant` does.
	 */
	@action
	async fetchProperties(tenantId?: number): Promise<void> {
		runInAction(() => {
			this.status.fetchProperties = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Property`
					: `${DIRECTORY_API.GROUPS}?label[]=Property`
			);
			runInAction(() => {
				this.properties = data;
				this.propertiesTenantId = tenantId;
				this.status.fetchProperties = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchProperties = Status.Failure;
			});
			throw e;
		}
	}

	// One tenant's properties, addressed the way fetchGroupsByProperty walks
	// down from its parent group.
	@action
	async fetchPropertiesByTenant(tenantId: number): Promise<void> {
		if (!tenantId) {
			runInAction(() => {
				this.tenantProperties = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchPropertiesByTenant = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Property`
			);
			runInAction(() => {
				this.tenantProperties = data;
				this.status.fetchPropertiesByTenant = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchPropertiesByTenant = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchRoles(): Promise<void> {
		runInAction(() => {
			this.status.fetchRoles = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryRole[]>(
				DIRECTORY_API.ROLES
			);
			runInAction(() => {
				this.roles = data;
				this.status.fetchRoles = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchRoles = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchGroupsByProperty(propertyIds: number[]): Promise<void> {
		if (!propertyIds.length) {
			runInAction(() => {
				this.groups = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchGroups = Status.Pending;
		});
		try {
			const ids = propertyIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${ids}&include_group_tree=true&label[]=Group`
			);
			runInAction(() => {
				this.groups = data;
				this.status.fetchGroups = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchGroups = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByProperty(propertyIds: number[]): Promise<void> {
		if (!propertyIds.length) {
			runInAction(() => {
				this.users = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsers = Status.Pending;
		});
		try {
			const ids = propertyIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryUser[]>(
				`${DIRECTORY_API.USERS}?group_id=${ids}&include_group_tree=true`
			);
			runInAction(() => {
				this.users = data;
				this.status.fetchUsers = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByIds(userIds: string[]): Promise<void> {
		if (!userIds.length) {
			runInAction(() => {
				this.assignmentUsers = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsersByIds = Status.Pending;
		});
		try {
			const ids = userIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryUser[]>(
				`${DIRECTORY_API.USERS}?id=${ids}&include_group_tree=false`
			);
			runInAction(() => {
				this.assignmentUsers = data;
				this.status.fetchUsersByIds = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchUsersByIds = Status.Failure;
			});
			throw e;
		}
	}
}

export const assignUsersStore = new AssignUsersStore();
export default AssignUsersStore;
